/* eslint-disable no-loss-of-precision */
// Paper.js Boolean Operations - Ported from Paper.js v0.12 Original: Copyright (c) 2011-2020, Jürg Lehni & Jonathan Puckey Distributed under the MIT license. See LICENSE file for details.
//
// Adapted for curve8 format: [ax, ay, cp1x, cp1y, cp2x, cp2y, bx, by] This file contains the complete Paper.js boolean pipeline:
// Numerical → Curve utilities → Intersection finder → CurveLocation → CollisionDetection → Wrappers → getWinding → propagateWinding → divideLocations → tracePaths → reorientPaths → traceBoolean

// ─── Numerical primitives ───────────────────────────────────────────

const abs = Math.abs
const sqrt = Math.sqrt
const pow = Math.pow
const min = Math.min
const max = Math.max
const log2 = Math.log2 || (x => Math.log(x) * Math.LOG2E)

const EPSILON = 1e-12
const MACHINE_EPSILON = 1.12e-16
const CURVETIME_EPSILON = 1e-8
const GEOMETRIC_EPSILON = 1e-7
const TRIGONOMETRIC_EPSILON = 1e-8

function isZero(val) {
	return val >= -EPSILON && val <= EPSILON
}

function clamp(value, lo, hi) {
	return value < lo ? lo : value > hi ? hi : value
}

// Gauss-Legendre abscissas and weights for n = 2..16
const abscissas = [
	[0.5773502691896257645091488],
	[0, 0.7745966692414833770358531],
	[0.3399810435848562648026658, 0.8611363115940525752239465],
	[0, 0.5384693101056830910363144, 0.9061798459386639927976269],
	[0.2386191860831969086305017, 0.6612093864662645136613996, 0.9324695142031520278123016],
	[0, 0.4058451513773971669066064, 0.7415311855993944398638648, 0.9491079123427585245261897],
	[0.1834346424956498049394761, 0.525532409916328985817739, 0.7966664774136267395915539, 0.9602898564975362316835609],
	[
		0, 0.324253423403808929038538, 0.613371432700590397308702, 0.8360311073266357942994298,
		0.9681602395076260898355762,
	],
	[
		0.148874338981631210884826, 0.4333953941292471907992659, 0.6794095682990244062343274,
		0.8650633666889845107320967, 0.973906528517171720077964,
	],
	[
		0, 0.269543155952344972331532, 0.5190961292068118159257257, 0.7301520055740493240934163,
		0.8870625997680952990751578, 0.978228658146056992803938,
	],
	[
		0.1252334085114689154724414, 0.3678314989981801937526915, 0.5873179542866174472967024,
		0.7699026741943046870368938, 0.9041172563704748566784659, 0.9815606342467192506905491,
	],
	[
		0, 0.2304583159551347940655281, 0.4484927510364468528779129, 0.6423493394403402206439846,
		0.8015780907333099127942065, 0.9175983992229779652065478, 0.9841830547185881494728294,
	],
	[
		0.1080549487073436620662447, 0.3191123689278897604356718, 0.5152486363581540919652907,
		0.6872929048116854701480198, 0.8272013150697649931897947, 0.9284348836635735173363911,
		0.9862838086968123388415973,
	],
	[
		0, 0.2011940939974345223006283, 0.3941513470775633698972074, 0.5709721726085388475372267,
		0.7244177313601700474161861, 0.8482065834104272162006483, 0.9372733924007059043077589,
		0.9879925180204854284895657,
	],
	[
		0.0950125098376374401853193, 0.2816035507792589132304605, 0.4580167776572273863424194,
		0.6178762444026437484466718, 0.7554044083550030338951012, 0.8656312023878317438804679,
		0.9445750230732325760779884, 0.9894009349916499325961542,
	],
]

const weights = [
	[1],
	[0.8888888888888888888888889, 0.5555555555555555555555556],
	[0.6521451548625461426269361, 0.3478548451374538573730639],
	[0.5688888888888888888888889, 0.4786286704993664680412915, 0.236926885056189087514264],
	[0.4679139345726910473898703, 0.3607615730481386075698335, 0.1713244923791703450402961],
	[0.417959183673469387755102, 0.3818300505051189449503698, 0.2797053914892766679014678, 0.1294849661688696932706114],
	[0.3626837833783619829651504, 0.3137066458778872873379622, 0.222381034453374470544356, 0.1012285362903762591525314],
	[
		0.3302393550012597631645251, 0.3123470770400028400686304, 0.2606106964029354623187429,
		0.180648160694857404058472, 0.0812743883615744119718922,
	],
	[
		0.295524224714752870173893, 0.2692667193099963550912269, 0.2190863625159820439955349,
		0.1494513491505805931457763, 0.0666713443086881375935688,
	],
	[
		0.2729250867779006307144835, 0.2628045445102466621806889, 0.2331937645919904799185237,
		0.1862902109277342514260976, 0.1255803694649046246346943, 0.0556685671161736664827537,
	],
	[
		0.2491470458134027850005624, 0.2334925365383548087608499, 0.2031674267230659217490645,
		0.1600783285433462263346525, 0.1069393259953184309602547, 0.047175336386511827194616,
	],
	[
		0.2325515532308739101945895, 0.2262831802628972384120902, 0.2078160475368885023125232,
		0.1781459807619457382800467, 0.1388735102197872384636018, 0.0921214998377284479144218,
		0.0404840047653158795200216,
	],
	[
		0.2152638534631577901958764, 0.2051984637212956039659241, 0.1855383974779378137417166,
		0.1572031671581935345696019, 0.1215185706879031846894148, 0.0801580871597602098056333,
		0.0351194603317518630318329,
	],
	[
		0.2025782419255612728806202, 0.1984314853271115764561183, 0.1861610000155622110268006,
		0.1662692058169939335532009, 0.1395706779261543144478048, 0.1071592204671719350118695,
		0.0703660474881081247092674, 0.0307532419961172683546284,
	],
	[
		0.1894506104550684962853967, 0.1826034150449235888667637, 0.1691565193950025381893121,
		0.1495959888165767320815017, 0.1246289712555338720524763, 0.0951585116824927848099251,
		0.0622535239386478928628438, 0.0271524594117540948517806,
	],
]

function getDiscriminant(a, b, c) {
	function split(v) {
		const x = v * 134217729,
			y = v - x,
			hi = y + x,
			lo = v - hi
		return [hi, lo]
	}
	let D = b * b - a * c,
		E = b * b + a * c
	if (abs(D) * 3 < E) {
		const ad = split(a),
			bd = split(b),
			cd = split(c),
			p = b * b,
			dp = bd[0] * bd[0] - p + 2 * bd[0] * bd[1] + bd[1] * bd[1],
			q = a * c,
			dq = ad[0] * cd[0] - q + ad[0] * cd[1] + ad[1] * cd[0] + ad[1] * cd[1]
		D = p - q + (dp - dq)
	}
	return D
}

function getNormalizationFactor() {
	const norm = Math.max.apply(Math, arguments)
	return norm && (norm < 1e-8 || norm > 1e8) ? pow(2, -Math.round(log2(norm))) : 0
}

function integrate(f, a, b, n) {
	let x = abscissas[n - 2],
		w = weights[n - 2],
		A = (b - a) * 0.5,
		B = A + a,
		i = 0,
		m = (n + 1) >> 1,
		sum = n & 1 ? w[i++] * f(B) : 0
	while (i < m) {
		const Ax = A * x[i]
		sum += w[i++] * (f(B + Ax) + f(B - Ax))
	}
	return A * sum
}

function findRoot(f, df, x, a, b, n, tolerance) {
	for (let i = 0; i < n; i++) {
		const fx = f(x),
			dx = fx / df(x),
			nx = x - dx
		if (abs(dx) < tolerance) {
			x = nx
			break
		}
		if (fx > 0) {
			b = x
			x = nx <= a ? (a + b) * 0.5 : nx
		} else {
			a = x
			x = nx >= b ? (a + b) * 0.5 : nx
		}
	}
	return clamp(x, a, b)
}

function solveQuadratic(a, b, c, roots, mn, mx) {
	let x1,
		x2 = Infinity
	if (abs(a) < EPSILON) {
		if (abs(b) < EPSILON) return abs(c) < EPSILON ? -1 : 0
		x1 = -c / b
	} else {
		b *= -0.5
		let D = getDiscriminant(a, b, c)
		if (D && abs(D) < MACHINE_EPSILON) {
			const f = getNormalizationFactor(abs(a), abs(b), abs(c))
			if (f) {
				a *= f
				b *= f
				c *= f
				D = getDiscriminant(a, b, c)
			}
		}
		if (D >= -MACHINE_EPSILON) {
			const Q = D < 0 ? 0 : sqrt(D),
				R = b + (b < 0 ? -Q : Q)
			if (R === 0) {
				x1 = c / a
				x2 = -x1
			} else {
				x1 = R / a
				x2 = c / R
			}
		}
	}
	let count = 0,
		boundless = mn == null,
		minB = mn - EPSILON,
		maxB = mx + EPSILON
	if (isFinite(x1) && (boundless || (x1 > minB && x1 < maxB))) roots[count++] = boundless ? x1 : clamp(x1, mn, mx)
	if (x2 !== x1 && isFinite(x2) && (boundless || (x2 > minB && x2 < maxB)))
		roots[count++] = boundless ? x2 : clamp(x2, mn, mx)
	return count
}

function solveCubic(a, b, c, d, roots, mn, mx) {
	let f = getNormalizationFactor(abs(a), abs(b), abs(c), abs(d)),
		x,
		b1,
		c2,
		qd,
		q
	if (f) {
		a *= f
		b *= f
		c *= f
		d *= f
	}

	function evaluate(x0) {
		x = x0
		const tmp = a * x
		b1 = tmp + b
		c2 = b1 * x + c
		qd = (tmp + b1) * x + c2
		q = c2 * x + d
	}

	if (abs(a) < EPSILON) {
		a = b
		b1 = c
		c2 = d
		x = Infinity
	} else if (abs(d) < EPSILON) {
		b1 = b
		c2 = c
		x = 0
	} else {
		evaluate(-(b / a) / 3)
		let t = q / a,
			r = pow(abs(t), 1 / 3),
			s = t < 0 ? -1 : 1,
			td = -qd / a,
			rd = td > 0 ? 1.324717957244746 * Math.max(r, sqrt(td)) : r,
			x0 = x - s * rd
		if (x0 !== x) {
			do {
				evaluate(x0)
				x0 = qd === 0 ? x : x - q / qd / (1 + MACHINE_EPSILON)
			} while (s * x0 > s * x)
			if (abs(a) * x * x > abs(d / x)) {
				c2 = -d / x
				b1 = (c2 - c) / x
			}
		}
	}
	let count = solveQuadratic(a, b1, c2, roots, mn, mx),
		boundless = mn == null
	if (
		isFinite(x) &&
		(count === 0 || (count > 0 && x !== roots[0] && x !== roots[1])) &&
		(boundless || (x > mn - EPSILON && x < mx + EPSILON))
	)
		roots[count++] = boundless ? x : clamp(x, mn, mx)
	return count
}

// ─── Curve utilities ────────────────────────────────────────────────

function curveSubdivide(v, t) {
	const x0 = v[0],
		y0 = v[1],
		x1 = v[2],
		y1 = v[3],
		x2 = v[4],
		y2 = v[5],
		x3 = v[6],
		y3 = v[7]
	if (t === undefined) t = 0.5
	const u = 1 - t,
		x4 = u * x0 + t * x1,
		y4 = u * y0 + t * y1,
		x5 = u * x1 + t * x2,
		y5 = u * y1 + t * y2,
		x6 = u * x2 + t * x3,
		y6 = u * y2 + t * y3,
		x7 = u * x4 + t * x5,
		y7 = u * y4 + t * y5,
		x8 = u * x5 + t * x6,
		y8 = u * y5 + t * y6,
		x9 = u * x7 + t * x8,
		y9 = u * y7 + t * y8
	return [
		[x0, y0, x4, y4, x7, y7, x9, y9],
		[x9, y9, x8, y8, x6, y6, x3, y3],
	]
}

function curveSolveCubic(v, coord, val, roots, mn, mx) {
	let v0 = v[coord],
		v1 = v[coord + 2],
		v2 = v[coord + 4],
		v3 = v[coord + 6],
		res = 0
	if (!((v0 < val && v3 < val && v1 < val && v2 < val) || (v0 > val && v3 > val && v1 > val && v2 > val))) {
		const c = 3 * (v1 - v0),
			b = 3 * (v2 - v1) - c,
			a = v3 - v0 - c - b
		res = solveCubic(a, b, c, v0 - val, roots, mn, mx)
	}
	return res
}

function curveEvaluate(v, t, type, normalized) {
	if (t == null || t < 0 || t > 1) return null
	let x0 = v[0],
		y0 = v[1],
		x1 = v[2],
		y1 = v[3],
		x2 = v[4],
		y2 = v[5],
		x3 = v[6],
		y3 = v[7]
	if (isZero(x1 - x0) && isZero(y1 - y0)) {
		x1 = x0
		y1 = y0
	}
	if (isZero(x2 - x3) && isZero(y2 - y3)) {
		x2 = x3
		y2 = y3
	}
	let cx = 3 * (x1 - x0),
		bx = 3 * (x2 - x1) - cx,
		ax = x3 - x0 - cx - bx,
		cy = 3 * (y1 - y0),
		by = 3 * (y2 - y1) - cy,
		ay = y3 - y0 - cy - by,
		x,
		y
	if (type === 0) {
		x = t === 0 ? x0 : t === 1 ? x3 : ((ax * t + bx) * t + cx) * t + x0
		y = t === 0 ? y0 : t === 1 ? y3 : ((ay * t + by) * t + cy) * t + y0
	} else {
		const tMin = CURVETIME_EPSILON,
			tMax = 1 - tMin
		if (t < tMin) {
			x = cx
			y = cy
		} else if (t > tMax) {
			x = 3 * (x3 - x2)
			y = 3 * (y3 - y2)
		} else {
			x = (3 * ax * t + 2 * bx) * t + cx
			y = (3 * ay * t + 2 * by) * t + cy
		}
		if (normalized) {
			if (x === 0 && y === 0 && (t < tMin || t > tMax)) {
				x = x2 - x1
				y = y2 - y1
			}
			const len = sqrt(x * x + y * y)
			if (len) {
				x /= len
				y /= len
			}
		}
		if (type === 3) {
			const x2d = 6 * ax * t + 2 * bx,
				y2d = 6 * ay * t + 2 * by,
				d = pow(x * x + y * y, 3 / 2)
			x = d !== 0 ? (x * y2d - y * x2d) / d : 0
			y = 0
		}
	}
	return type === 2 ? { x: y, y: -x } : { x, y }
}

function curveGetPoint(v, t) {
	return curveEvaluate(v, t, 0, false)
}

function curveGetTangent(v, t) {
	return curveEvaluate(v, t, 1, true)
}

function curveIsStraight(v) {
	const x0 = v[0],
		y0 = v[1],
		x3 = v[6],
		y3 = v[7],
		h1x = v[2] - x0,
		h1y = v[3] - y0,
		h2x = v[4] - x3,
		h2y = v[5] - y3
	if (h1x === 0 && h1y === 0 && h2x === 0 && h2y === 0) return true
	const vx = x3 - x0,
		vy = y3 - y0
	if (vx === 0 && vy === 0) return false
	// Check collinearity via cross product
	if (abs(vx * h1y - vy * h1x) > GEOMETRIC_EPSILON) return false
	if (abs(vx * h2y - vy * h2x) > GEOMETRIC_EPSILON) return false
	// Check handles are within segment using line distance
	const len2 = vx * vx + vy * vy
	// Project handles onto line
	const s1 = (vx * h1x + vy * h1y) / len2
	const s2 = (vx * h2x + vy * h2y) / len2
	return s1 >= 0 && s1 <= 1 && s2 <= 0 && s2 >= -1
}

function curveGetMonoCurves(v, dir) {
	const curves = [],
		io = dir ? 0 : 1,
		o0 = v[io],
		o1 = v[io + 2],
		o2 = v[io + 4],
		o3 = v[io + 6]
	if ((o0 >= o1 === o1 >= o2 && o1 >= o2 === o2 >= o3) || curveIsStraight(v)) {
		curves.push(v)
	} else {
		const a = 3 * (o1 - o2) - o0 + o3,
			b = 2 * (o0 + o2) - 4 * o1,
			c = o1 - o0,
			tMin = CURVETIME_EPSILON,
			tMax = 1 - tMin,
			roots = [],
			n = solveQuadratic(a, b, c, roots, tMin, tMax)
		if (!n) {
			curves.push(v)
		} else {
			roots.sort()
			let t = roots[0],
				parts = curveSubdivide(v, t)
			curves.push(parts[0])
			if (n > 1) {
				t = (roots[1] - t) / (1 - t)
				parts = curveSubdivide(parts[1], t)
				curves.push(parts[0])
			}
			curves.push(parts[1])
		}
	}
	return curves
}

function curveGetArea(v) {
	const x0 = v[0],
		y0 = v[1],
		x1 = v[2],
		y1 = v[3],
		x2 = v[4],
		y2 = v[5],
		x3 = v[6],
		y3 = v[7]
	return (
		(3 *
			((y3 - y0) * (x1 + x2) -
				(x3 - x0) * (y1 + y2) +
				y1 * (x0 - x2) -
				x1 * (y0 - y2) +
				y3 * (x2 + x0 / 3) -
				x3 * (y2 + y0 / 3))) /
		20
	)
}

function getLengthIntegrand(v) {
	const x0 = v[0],
		y0 = v[1],
		x1 = v[2],
		y1 = v[3],
		x2 = v[4],
		y2 = v[5],
		x3 = v[6],
		y3 = v[7],
		ax = 9 * (x1 - x2) + 3 * (x3 - x0),
		bx = 6 * (x0 + x2) - 12 * x1,
		cx = 3 * (x1 - x0),
		ay = 9 * (y1 - y2) + 3 * (y3 - y0),
		by = 6 * (y0 + y2) - 12 * y1,
		cy = 3 * (y1 - y0)
	return t => {
		const dx = (ax * t + bx) * t + cx,
			dy = (ay * t + by) * t + cy
		return sqrt(dx * dx + dy * dy)
	}
}

function getIterations(a, b) {
	return Math.max(2, Math.min(16, Math.ceil(abs(b - a) * 32)))
}

function curveGetLength(v, a, b, ds) {
	if (a === undefined) a = 0
	if (b === undefined) b = 1
	if (curveIsStraight(v)) {
		let c = v
		if (b < 1) {
			c = curveSubdivide(c, b)[0]
			a /= b
		}
		if (a > 0) {
			c = curveSubdivide(c, a)[1]
		}
		const dx = c[6] - c[0],
			dy = c[7] - c[1]
		return sqrt(dx * dx + dy * dy)
	}
	return integrate(ds || getLengthIntegrand(v), a, b, getIterations(a, b))
}

function curveGetTimeAt(v, offset, start) {
	if (start === undefined) start = offset < 0 ? 1 : 0
	if (offset === 0) return start
	const forward = offset > 0,
		a = forward ? start : 0,
		b = forward ? 1 : start,
		ds = getLengthIntegrand(v),
		rangeLength = curveGetLength(v, a, b, ds),
		diff = abs(offset) - rangeLength
	if (abs(diff) < EPSILON) return forward ? b : a
	if (diff > EPSILON) return null
	let guess = offset / rangeLength,
		length = 0
	function f(t) {
		length += integrate(ds, start, t, getIterations(start, t))
		start = t
		return length - offset
	}
	return findRoot(f, ds, start + guess, a, b, 32, EPSILON)
}

function curveGetPart(v, from, to) {
	const flip = from > to
	if (flip) {
		const tmp = from
		from = to
		to = tmp
	}
	if (from > 0) v = curveSubdivide(v, from)[1]
	if (to < 1) v = curveSubdivide(v, (to - from) / (1 - from))[0]
	return flip ? [v[6], v[7], v[4], v[5], v[2], v[3], v[0], v[1]] : v
}

function curveClassify(v) {
	let x0 = v[0],
		y0 = v[1],
		x1 = v[2],
		y1 = v[3],
		x2 = v[4],
		y2 = v[5],
		x3 = v[6],
		y3 = v[7],
		a1 = x0 * (y3 - y2) + y0 * (x2 - x3) + x3 * y2 - y3 * x2,
		a2 = x1 * (y0 - y3) + y1 * (x3 - x0) + x0 * y3 - y0 * x3,
		a3 = x2 * (y1 - y0) + y2 * (x0 - x1) + x1 * y0 - y1 * x0,
		d3 = 3 * a3,
		d2 = d3 - a2,
		d1 = d2 - a2 + a1,
		l = sqrt(d1 * d1 + d2 * d2 + d3 * d3),
		s = l !== 0 ? 1 / l : 0
	d1 *= s
	d2 *= s
	d3 *= s

	function type(tp, t1, t2) {
		let hasRoots = t1 !== undefined,
			t1Ok = hasRoots && t1 > 0 && t1 < 1,
			t2Ok = hasRoots && t2 > 0 && t2 < 1
		if (hasRoots && (!(t1Ok || t2Ok) || (tp === 'loop' && !(t1Ok && t2Ok)))) {
			tp = 'arch'
			t1Ok = t2Ok = false
		}
		return {
			type: tp,
			roots: t1Ok || t2Ok ? (t1Ok && t2Ok ? (t1 < t2 ? [t1, t2] : [t2, t1]) : [t1Ok ? t1 : t2]) : null,
		}
	}

	if (isZero(d1)) {
		return isZero(d2) ? type(isZero(d3) ? 'line' : 'quadratic') : type('serpentine', d3 / (3 * d2))
	}
	const d = 3 * d2 * d2 - 4 * d1 * d3
	if (isZero(d)) return type('cusp', d2 / (2 * d1))
	const f1 = d > 0 ? sqrt(d / 3) : sqrt(-d),
		f2 = 2 * d1
	return type(d > 0 ? 'serpentine' : 'loop', (d2 + f1) / f2, (d2 - f1) / f2)
}

function curveGetPeaks(v) {
	const x0 = v[0],
		y0 = v[1],
		x1 = v[2],
		y1 = v[3],
		x2 = v[4],
		y2 = v[5],
		x3 = v[6],
		y3 = v[7],
		ax = -x0 + 3 * x1 - 3 * x2 + x3,
		bx = 3 * x0 - 6 * x1 + 3 * x2,
		cx = -3 * x0 + 3 * x1,
		ay = -y0 + 3 * y1 - 3 * y2 + y3,
		by = 3 * y0 - 6 * y1 + 3 * y2,
		cy = -3 * y0 + 3 * y1,
		tMin = CURVETIME_EPSILON,
		tMax = 1 - tMin,
		roots = []
	solveCubic(
		9 * (ax * ax + ay * ay),
		9 * (ax * bx + by * ay),
		2 * (bx * bx + by * by) + 3 * (cx * ax + cy * ay),
		cx * bx + by * cy,
		roots,
		tMin,
		tMax
	)
	return roots.sort()
}

function curveGetTimeOf(v, px, py) {
	const epsilon = EPSILON,
		geomEpsilon = GEOMETRIC_EPSILON
	// Check endpoints first
	if (abs(v[0] - px) < epsilon && abs(v[1] - py) < epsilon) return 0
	if (abs(v[6] - px) < epsilon && abs(v[7] - py) < epsilon) return 1
	const coords = [px, py],
		roots = []
	for (let c = 0; c < 2; c++) {
		const count = curveSolveCubic(v, c, coords[c], roots, 0, 1)
		for (let i = 0; i < count; i++) {
			const u = roots[i],
				pt = curveGetPoint(v, u)
			if (abs(pt.x - px) < geomEpsilon && abs(pt.y - py) < geomEpsilon) return u
		}
	}
	// Fall back with geometric epsilon for endpoints
	if (abs(v[0] - px) < geomEpsilon && abs(v[1] - py) < geomEpsilon) return 0
	if (abs(v[6] - px) < geomEpsilon && abs(v[7] - py) < geomEpsilon) return 1
	return null
}

function _curveIsFlatEnough(v, flatness) {
	const x0 = v[0],
		y0 = v[1],
		x1 = v[2],
		y1 = v[3],
		x2 = v[4],
		y2 = v[5],
		x3 = v[6],
		y3 = v[7],
		ux = 3 * x1 - 2 * x0 - x3,
		uy = 3 * y1 - 2 * y0 - y3,
		vx = 3 * x2 - 2 * x3 - x0,
		vy = 3 * y2 - 2 * y3 - y0
	return max(ux * ux, vx * vx) + max(uy * uy, vy * vy) <= 16 * flatness * flatness
}

// ─── Line utilities (needed by intersection) ────────────────────────

function lineGetSignedDistance(px, py, vx, vy, x, y, asVector) {
	if (!asVector) {
		vx -= px
		vy -= py
	}
	return vx === 0 && vy === 0
		? 0 // degenerate
		: (vx * (y - py) - vy * (x - px)) / sqrt(vx * vx + vy * vy)
}

function lineGetDistance(px, py, vx, vy, x, y, asVector) {
	return abs(lineGetSignedDistance(px, py, vx, vy, x, y, asVector))
}

function lineIntersect(x1, y1, x2, y2, x3, y3, x4, y4) {
	const vx = x2 - x1,
		vy = y2 - y1,
		wx = x4 - x3,
		wy = y4 - y3,
		cross = vx * wy - vy * wx
	if (!isZero(cross)) {
		let dx = x1 - x3,
			dy = y1 - y3,
			u1 = (wx * dy - wy * dx) / cross,
			u2 = (vx * dy - vy * dx) / cross,
			uEps = -EPSILON,
			uMax = 1 + EPSILON
		if (u1 >= uEps && u1 <= uMax && u2 >= uEps && u2 <= uMax) {
			u1 = clamp(u1, 0, 1)
			return { x: x1 + u1 * vx, y: y1 + u1 * vy }
		}
	}
	return null
}

// ─── Collision detection ────────────────────────────────────────────

function findBoundsCollisions(boundsA, boundsB, tolerance, sweepVertical, onlySweepAxisCollisions) {
	const self = !boundsB || boundsA === boundsB,
		allBounds = self ? boundsA : boundsA.concat(boundsB),
		lengthA = boundsA.length,
		lengthAll = allBounds.length

	function binarySearch(indices, coord, value) {
		let lo = 0,
			hi = indices.length
		while (lo < hi) {
			const mid = (hi + lo) >>> 1
			if (allBounds[indices[mid]][coord] < value) lo = mid + 1
			else hi = mid
		}
		return lo - 1
	}

	const pri0 = sweepVertical ? 1 : 0,
		pri1 = pri0 + 2,
		sec0 = sweepVertical ? 0 : 1,
		sec1 = sec0 + 2

	const allIndicesByPri0 = new Array(lengthAll)
	for (let i = 0; i < lengthAll; i++) allIndicesByPri0[i] = i
	allIndicesByPri0.sort((i1, i2) => allBounds[i1][pri0] - allBounds[i2][pri0])

	const activeIndicesByPri1 = [],
		allCollisions = new Array(lengthA)
	for (let i = 0; i < lengthAll; i++) {
		let curIndex = allIndicesByPri0[i],
			curBounds = allBounds[curIndex],
			origIndex = self ? curIndex : curIndex - lengthA,
			isCurrentA = curIndex < lengthA,
			isCurrentB = self || !isCurrentA,
			curCollisions = isCurrentA ? [] : null
		if (activeIndicesByPri1.length) {
			const pruneCount = binarySearch(activeIndicesByPri1, pri1, curBounds[pri0] - tolerance) + 1
			activeIndicesByPri1.splice(0, pruneCount)
			if (self && onlySweepAxisCollisions) {
				curCollisions = curCollisions.concat(activeIndicesByPri1)
				for (let j = 0; j < activeIndicesByPri1.length; j++) {
					allCollisions[activeIndicesByPri1[j]].push(origIndex)
				}
			} else {
				const curSec1 = curBounds[sec1],
					curSec0 = curBounds[sec0]
				for (let j = 0; j < activeIndicesByPri1.length; j++) {
					const activeIndex = activeIndicesByPri1[j],
						activeBounds = allBounds[activeIndex],
						isActiveA = activeIndex < lengthA,
						isActiveB = self || activeIndex >= lengthA
					if (
						onlySweepAxisCollisions ||
						(((isCurrentA && isActiveB) || (isCurrentB && isActiveA)) &&
							curSec1 >= activeBounds[sec0] - tolerance &&
							curSec0 <= activeBounds[sec1] + tolerance)
					) {
						if (isCurrentA && isActiveB) curCollisions.push(self ? activeIndex : activeIndex - lengthA)
						if (isCurrentB && isActiveA) allCollisions[activeIndex].push(origIndex)
					}
				}
			}
		}
		if (isCurrentA) {
			if (boundsA === boundsB) curCollisions.push(curIndex)
			allCollisions[curIndex] = curCollisions
		}
		if (activeIndicesByPri1.length) {
			const curPri1 = curBounds[pri1],
				index = binarySearch(activeIndicesByPri1, pri1, curPri1)
			activeIndicesByPri1.splice(index + 1, 0, curIndex)
		} else {
			activeIndicesByPri1.push(curIndex)
		}
	}
	for (let i = 0; i < allCollisions.length; i++) {
		const col = allCollisions[i]
		if (col) col.sort((a, b) => a - b)
	}
	return allCollisions
}

function findCurveBoundsCollisions(curves1, curves2, tolerance, bothAxis) {
	function getBounds(curves) {
		const bounds = new Array(curves.length)
		for (let i = 0; i < curves.length; i++) {
			const v = curves[i]
			bounds[i] = [
				min(v[0], v[2], v[4], v[6]),
				min(v[1], v[3], v[5], v[7]),
				max(v[0], v[2], v[4], v[6]),
				max(v[1], v[3], v[5], v[7]),
			]
		}
		return bounds
	}
	const bounds1 = getBounds(curves1),
		bounds2 = !curves2 || curves2 === curves1 ? bounds1 : getBounds(curves2)
	if (bothAxis) {
		const hor = findBoundsCollisions(bounds1, bounds2, tolerance || 0, false, true),
			ver = findBoundsCollisions(bounds1, bounds2, tolerance || 0, true, true),
			list = []
		for (let i = 0, l = hor.length; i < l; i++) list[i] = { hor: hor[i], ver: ver[i] }
		return list
	}
	return findBoundsCollisions(bounds1, bounds2, tolerance || 0)
}

function _findItemBoundsCollisions(items, items2, tolerance) {
	function getBounds(items) {
		const bounds = new Array(items.length)
		for (let i = 0; i < items.length; i++) {
			const b = items[i].getBounds()
			bounds[i] = [b.x, b.y, b.x + b.width, b.y + b.height]
		}
		return bounds
	}
	const bounds1 = getBounds(items),
		bounds2 = !items2 || items2 === items ? bounds1 : getBounds(items2)
	return findBoundsCollisions(bounds1, bounds2, tolerance || 0)
}

// ─── Wrapper data model ─────────────────────────────────────────────

let _nextId = 1

function createSegment(point, handleIn, handleOut) {
	return {
		_point: { x: point.x, y: point.y },
		_handleIn: handleIn ? { x: handleIn.x, y: handleIn.y } : { x: 0, y: 0 },
		_handleOut: handleOut ? { x: handleOut.x, y: handleOut.y } : { x: 0, y: 0 },
		_index: 0,
		_path: null,
		_intersection: null,
		_winding: null,
		_visited: false,
		getNext() {
			const segs = this._path && this._path._segments
			if (!segs) return null
			const next = this._index + 1
			return segs[next < segs.length ? next : this._path._closed ? 0 : -1] || null
		},
		getPrevious() {
			const segs = this._path && this._path._segments
			if (!segs) return null
			const prev = this._index - 1
			return segs[prev >= 0 ? prev : this._path._closed ? segs.length - 1 : -1] || null
		},
		getCurve() {
			const path = this._path
			if (!path) return null
			// Rebuild through getCurves() instead of reading _curves directly: removing a segment nulls the cache, so a raw read returns null for a still-valid path.
			const curves = path.getCurves()
			return (
				(curves &&
					(curves[this._index] ||
						(this._path._closed && this._index === path._segments.length - 1
							? curves[curves.length - 1]
							: null))) ||
				null
			)
		},
		isFirst() {
			return this._index === 0
		},
		isLast() {
			return this._path && this._index === this._path._segments.length - 1
		},
		remove() {
			if (this._path) {
				this._path._segments.splice(this._index, 1)
				this._path._reindex()
				this._path._curves = null
				this._path = null
			}
		},
	}
}

function createCurveWrapper(path, seg1, seg2) {
	return {
		_segment1: seg1,
		_segment2: seg2,
		_path: path,
		getValues() {
			const p1 = this._segment1._point,
				h1 = this._segment1._handleOut,
				h2 = this._segment2._handleIn,
				p2 = this._segment2._point
			return [p1.x, p1.y, p1.x + h1.x, p1.y + h1.y, p2.x + h2.x, p2.y + h2.y, p2.x, p2.y]
		},
		getIndex() {
			return this._segment1._index
		},
		getNext() {
			if (!this._path) return null
			const curves = this._path.getCurves()
			if (!curves.length) return null
			const idx = this._segment1._index + 1
			return curves[idx] || (this._path._closed && curves[0]) || null
		},
		getPrevious() {
			if (!this._path) return null
			const curves = this._path.getCurves()
			if (!curves.length) return null
			const idx = this._segment1._index - 1
			return curves[idx] || (this._path._closed && curves[curves.length - 1]) || null
		},
		getLength() {
			return curveGetLength(this.getValues())
		},
		getTimeAt(offset, start) {
			return curveGetTimeAt(this.getValues(), offset, start)
		},
		getPointAtTime(t) {
			return curveGetPoint(this.getValues(), t)
		},
		getTangentAtTime(t) {
			return curveGetTangent(this.getValues(), t)
		},
		getPointAt(offset) {
			return this.getPointAtTime(this.getTimeAt(offset))
		},
		hasHandles() {
			const h1 = this._segment1._handleOut,
				h2 = this._segment2._handleIn
			return !(h1.x === 0 && h1.y === 0 && h2.x === 0 && h2.y === 0)
		},
		isStraight() {
			return curveIsStraight(this.getValues())
		},
		hasLength(epsilon) {
			const p1 = this._segment1._point,
				p2 = this._segment2._point
			return (!(p1.x === p2.x && p1.y === p2.y) || this.hasHandles()) && this.getLength() > (epsilon || 0)
		},
		getPartLength(from, to) {
			return curveGetLength(this.getValues(), from, to)
		},
		clearHandles() {
			this._segment1._handleOut = { x: 0, y: 0 }
			this._segment2._handleIn = { x: 0, y: 0 }
		},
		divideAtTime(time, _setHandles) {
			const tMin = CURVETIME_EPSILON,
				tMax = 1 - tMin
			if (time < tMin || time > tMax) return null
			const parts = curveSubdivide(this.getValues(), time),
				left = parts[0],
				right = parts[1],
				setHandles = _setHandles || this.hasHandles(),
				seg1 = this._segment1,
				seg2 = this._segment2,
				path = this._path
			if (setHandles) {
				seg1._handleOut = { x: left[2] - left[0], y: left[3] - left[1] }
				seg2._handleIn = { x: right[4] - right[6], y: right[5] - right[7] }
			}
			const x = left[6],
				y = left[7],
				segment = createSegment(
					{ x, y },
					setHandles ? { x: left[4] - x, y: left[5] - y } : null,
					setHandles ? { x: right[2] - x, y: right[3] - y } : null
				)
			if (path) {
				path.insert(seg1._index + 1, segment)
				// Update this curve to represent the left half (seg1 → new segment)
				this._segment2 = segment
				return this.getNext()
			}
			return null
		},
		getLine() {
			const p1 = this._segment1._point,
				p2 = this._segment2._point
			return { px: p1.x, py: p1.y, vx: p2.x - p1.x, vy: p2.y - p1.y }
		},
		getTimeOf(point) {
			return curveGetTimeOf(this.getValues(), point.x, point.y)
		},
	}
}

function createPathWrapper(closed) {
	const path = {
		_id: _nextId++,
		_segments: [],
		_curves: null,
		_closed: closed !== undefined ? closed : true,
		_parent: null,
		_overlapsOnly: false,
		_reindex() {
			for (let i = 0; i < this._segments.length; i++) {
				this._segments[i]._index = i
				this._segments[i]._path = this
			}
			this._curves = null
		},
		getCurves() {
			if (!this._curves) {
				const segs = this._segments,
					n = segs.length,
					curves = []
				if (n > 1) {
					for (let i = 0; i < n - 1; i++) curves.push(createCurveWrapper(this, segs[i], segs[i + 1]))
					if (this._closed) curves.push(createCurveWrapper(this, segs[n - 1], segs[0]))
				}
				this._curves = curves
			}
			return this._curves
		},
		getFirstSegment() {
			return this._segments[0] || null
		},
		getLastSegment() {
			return this._segments[this._segments.length - 1] || null
		},
		getLastCurve() {
			const curves = this.getCurves()
			return curves[curves.length - 1] || null
		},
		isClockwise() {
			return this.getArea() > 0
		},
		setClockwise(cw) {
			if (this.isClockwise() !== cw) this.reverse()
		},
		getArea() {
			let curves = this.getCurves(),
				area = 0
			for (let i = 0; i < curves.length; i++) area += curveGetArea(curves[i].getValues())
			return area
		},
		getBounds() {
			const curves = this.getCurves()
			if (!curves.length) return { x: 0, y: 0, width: 0, height: 0 }
			let xMin = Infinity,
				yMin = Infinity,
				xMax = -Infinity,
				yMax = -Infinity
			for (let i = 0; i < curves.length; i++) {
				const v = curves[i].getValues()
				for (let j = 0; j < 8; j += 2) {
					if (v[j] < xMin) xMin = v[j]
					if (v[j] > xMax) xMax = v[j]
					if (v[j + 1] < yMin) yMin = v[j + 1]
					if (v[j + 1] > yMax) yMax = v[j + 1]
				}
			}
			return { x: xMin, y: yMin, width: xMax - xMin, height: yMax - yMin }
		},
		getInteriorPoint() {
			const b = this.getBounds(),
				point = { x: b.x + b.width / 2, y: b.y + b.height / 2 }
			if (!this.contains(point)) {
				const curves = this.getCurves(),
					y = point.y,
					intercepts = [],
					roots = []
				for (let i = 0, l = curves.length; i < l; i++) {
					const v = curves[i].getValues(),
						o0 = v[1],
						o1 = v[3],
						o2 = v[5],
						o3 = v[7]
					if (y >= min(o0, o1, o2, o3) && y <= max(o0, o1, o2, o3)) {
						const monoCurves = curveGetMonoCurves(v)
						for (let j = 0, m = monoCurves.length; j < m; j++) {
							const mv = monoCurves[j],
								mo0 = mv[1],
								mo3 = mv[7]
							if (mo0 !== mo3 && ((y >= mo0 && y <= mo3) || (y >= mo3 && y <= mo0))) {
								const x =
									y === mo0
										? mv[0]
										: y === mo3
											? mv[6]
											: curveSolveCubic(mv, 1, y, roots, 0, 1) === 1
												? curveGetPoint(mv, roots[0]).x
												: (mv[0] + mv[6]) / 2
								intercepts.push(x)
							}
						}
					}
				}
				if (intercepts.length > 1) {
					intercepts.sort((a, b) => a - b)
					point.x = (intercepts[0] + intercepts[1]) / 2
				}
			}
			return point
		},
		contains(point) {
			return this._getWinding(point).winding !== 0
		},
		_getWinding(point, dir, closed) {
			return getWinding(point, this.getCurves(), dir, closed)
		},
		insert(index, segment) {
			segment._path = this
			segment._index = index
			this._segments.splice(index, 0, segment)
			this._reindex()
		},
		add(segment) {
			segment._path = this
			segment._index = this._segments.length
			this._segments.push(segment)
			this._curves = null
		},
		removeSegments(from) {
			if (from === undefined) from = 0
			const removed = this._segments.splice(from)
			for (let i = 0; i < removed.length; i++) {
				removed[i]._path = null
			}
			this._reindex()
			return removed
		},
		setClosed(closed) {
			this._closed = closed
			this._curves = null
		},
		reverse() {
			this._segments.reverse()
			for (let i = 0; i < this._segments.length; i++) {
				const seg = this._segments[i],
					tmp = seg._handleIn
				seg._handleIn = seg._handleOut
				seg._handleOut = tmp
			}
			this._reindex()
		},
		compare(other) {
			if (!other || this._segments.length !== other._segments.length) return false
			const segs1 = this._segments,
				segs2 = other._segments,
				n = segs1.length
			for (let i = 0; i < n; i++) {
				const s1 = segs1[i],
					s2 = segs2[i]
				if (
					abs(s1._point.x - s2._point.x) > GEOMETRIC_EPSILON ||
					abs(s1._point.y - s2._point.y) > GEOMETRIC_EPSILON ||
					abs(s1._handleIn.x - s2._handleIn.x) > GEOMETRIC_EPSILON ||
					abs(s1._handleIn.y - s2._handleIn.y) > GEOMETRIC_EPSILON ||
					abs(s1._handleOut.x - s2._handleOut.x) > GEOMETRIC_EPSILON ||
					abs(s1._handleOut.y - s2._handleOut.y) > GEOMETRIC_EPSILON
				)
					return false
			}
			return true
		},
		clone() {
			const p = createPathWrapper(this._closed)
			for (let i = 0; i < this._segments.length; i++) {
				const s = this._segments[i]
				p.add(
					createSegment(
						{ x: s._point.x, y: s._point.y },
						{ x: s._handleIn.x, y: s._handleIn.y },
						{ x: s._handleOut.x, y: s._handleOut.y }
					)
				)
			}
			return p
		},
		isEmpty() {
			return this._segments.length === 0
		},
	}
	return path
}

function createCompoundPathWrapper() {
	const compound = {
		_id: _nextId++,
		_children: [],
		getCurves() {
			const all = []
			for (let i = 0; i < this._children.length; i++) {
				const curves = this._children[i].getCurves()
				for (let j = 0; j < curves.length; j++) all.push(curves[j])
			}
			return all
		},
		isClockwise() {
			return this._children.length > 0 && this._children[0].isClockwise()
		},
		_getWinding(point, dir, closed) {
			return getWinding(point, this.getCurves(), dir, closed)
		},
		reverse() {
			for (let i = 0; i < this._children.length; i++) this._children[i].reverse()
		},
	}
	return compound
}

// ─── curve8 ↔ wrapper conversions ───────────────────────────────────

function curve8ToPath(contour) {
	const path = createPathWrapper(true)
	if (!contour.length) return path
	// First segment: start of first curve
	const c0 = contour[0]
	const lastC = contour[contour.length - 1]
	// handleIn for first segment comes from last curve's cp2
	path.add(
		createSegment(
			{ x: c0[0], y: c0[1] },
			{ x: lastC[4] - c0[0], y: lastC[5] - c0[1] },
			{ x: c0[2] - c0[0], y: c0[3] - c0[1] }
		)
	)
	for (let i = 1; i < contour.length; i++) {
		const c = contour[i],
			prevC = contour[i - 1]
		path.add(
			createSegment(
				{ x: c[0], y: c[1] },
				{ x: prevC[4] - c[0], y: prevC[5] - c[1] },
				{ x: c[2] - c[0], y: c[3] - c[1] }
			)
		)
	}
	path._reindex()
	return path
}

function contoursToCompound(contours) {
	if (contours.length === 1) {
		const p = curve8ToPath(contours[0])
		p._parent = null
		return p
	}
	const compound = createCompoundPathWrapper()
	for (let i = 0; i < contours.length; i++) {
		const child = curve8ToPath(contours[i])
		child._parent = compound
		compound._children.push(child)
	}
	return compound
}

function getPaths(path) {
	return path._children || [path]
}

function pathToContour(path) {
	const curves = path.getCurves(),
		contour = []
	for (let i = 0; i < curves.length; i++) {
		contour.push(curves[i].getValues())
	}
	return contour
}

function resultToContours(paths) {
	const result = []
	for (let i = 0; i < paths.length; i++) {
		if (paths[i]) {
			const children = paths[i]._children
			if (children) {
				for (let j = 0; j < children.length; j++) result.push(pathToContour(children[j]))
			} else {
				result.push(pathToContour(paths[i]))
			}
		}
	}
	return result
}

// ─── CurveLocation ──────────────────────────────────────────────────

function createCurveLocation(curve, time, point, overlap) {
	const loc = {
		_curve: curve,
		_time: time,
		_point: point || curveGetPoint(curve.getValues(), time),
		_overlap: !!overlap,
		_intersection: null,
		_segment: null,
		_next: null,
		_previous: null,
		getTime() {
			return this._time
		},
		getCurve() {
			return this._curve
		},
		getPoint() {
			return this._point
		},
		getPath() {
			return this._curve && this._curve._path
		},
		getIndex() {
			return this._curve && this._curve.getIndex()
		},
		getSegment() {
			return this._segment
		},
		hasOverlap() {
			return !!this._overlap
		},
		isTouching() {
			const inter = this._intersection
			if (!inter) return false
			const t1 = curveGetTangent(this._curve.getValues(), this._time),
				t2 = curveGetTangent(inter._curve.getValues(), inter._time)
			// Collinear check via cross product
			if (abs(t1.x * t2.y - t1.y * t2.x) < TRIGONOMETRIC_EPSILON) {
				const c1 = this._curve,
					c2 = inter._curve
				if (curveIsStraight(c1.getValues()) && curveIsStraight(c2.getValues())) {
					const l1 = c1.getLine(),
						l2 = c2.getLine()
					if (
						lineIntersect(
							l1.px,
							l1.py,
							l1.px + l1.vx,
							l1.py + l1.vy,
							l2.px,
							l2.py,
							l2.px + l2.vx,
							l2.py + l2.vy
						)
					)
						return false
				}
				return true
			}
			return false
		},
		isCrossing() {
			const inter = this._intersection
			if (!inter) return false
			const t1 = this._time,
				t2 = inter._time,
				tMin = CURVETIME_EPSILON,
				tMax = 1 - tMin,
				t1Inside = t1 >= tMin && t1 <= tMax,
				t2Inside = t2 >= tMin && t2 <= tMax
			if (t1Inside && t2Inside) return !this.isTouching()
			let c2 = this._curve,
				c1 = c2 && t1 < tMin ? c2.getPrevious() : c2,
				c4 = inter._curve,
				c3 = c4 && t2 < tMin ? c4.getPrevious() : c4
			if (t1 > tMax) c2 = c2.getNext()
			if (t2 > tMax) c4 = c4.getNext()
			if (!c1 || !c2 || !c3 || !c4) return false

			const offsets = []
			function addOffsets(curve, end) {
				const v = curve.getValues(),
					roots = curveClassify(v).roots || curveGetPeaks(v),
					count = roots.length,
					offset = curveGetLength(v, end && count ? roots[count - 1] : 0, !end && count ? roots[0] : 1)
				offsets.push(count ? offset : offset / 32)
			}

			if (!t1Inside) {
				addOffsets(c1, true)
				addOffsets(c2, false)
			}
			if (!t2Inside) {
				addOffsets(c3, true)
				addOffsets(c4, false)
			}

			const pt = this._point,
				offset = Math.min.apply(Math, offsets),
				v2 = t1Inside ? c2.getTangentAtTime(t1) : subtract2d(c2.getPointAt(offset), pt),
				v1 = t1Inside ? negate2d(v2) : subtract2d(c1.getPointAt(-offset), pt),
				v4 = t2Inside ? c4.getTangentAtTime(t2) : subtract2d(c4.getPointAt(offset), pt),
				v3 = t2Inside ? negate2d(v4) : subtract2d(c3.getPointAt(-offset), pt),
				a1 = Math.atan2(v1.y, v1.x),
				a2 = Math.atan2(v2.y, v2.x),
				a3 = Math.atan2(v3.y, v3.x),
				a4 = Math.atan2(v4.y, v4.x)

			function isInRange(angle, lo, hi) {
				return lo < hi ? angle > lo && angle < hi : angle > lo || angle < hi
			}

			return !!(t1Inside
				? isInRange(a1, a3, a4) ^ isInRange(a2, a3, a4) && isInRange(a1, a4, a3) ^ isInRange(a2, a4, a3)
				: isInRange(a3, a1, a2) ^ isInRange(a4, a1, a2) && isInRange(a3, a2, a1) ^ isInRange(a4, a2, a1))
		},
		_setSegment(segment) {
			const curve = segment.getCurve()
			if (curve) {
				this._curve = curve
				this._path = curve._path
			}
			this._segment = segment
			this._time = segment === curve?._segment1 ? 0 : 1
			this._point = { x: segment._point.x, y: segment._point.y }
		},
		_setCurve(curve) {
			this._curve = curve
			this._path = curve._path
			this._segment = null
		},
		equals(loc, _ignoreOther) {
			if (this === loc) return true
			if (!loc) return false
			const c1 = this._curve,
				c2 = loc._curve
			if (!c1 || !c2) return false
			const p1 = c1._path,
				p2 = c2._path
			if (p1 !== p2) return false
			const diff = abs(this.getIndex() + this._time - (loc.getIndex() + loc._time))
			const epsilon = GEOMETRIC_EPSILON
			if (diff < epsilon) {
				if (_ignoreOther) return true
				const i1 = this._intersection,
					i2 = loc._intersection
				return (!i1 && !i2) || (i1 && i2 && i1.equals(i2, true))
			}
			return false
		},
	}
	// Merge near-end intersections to start of next curve
	if (time >= 1 - CURVETIME_EPSILON) {
		const next = curve.getNext()
		if (next) {
			loc._time = 0
			loc._curve = next
			loc._point = point || curveGetPoint(next.getValues(), 0)
		}
	}
	return loc
}

function subtract2d(a, b) {
	return { x: a.x - b.x, y: a.y - b.y }
}

function negate2d(v) {
	return { x: -v.x, y: -v.y }
}

// CurveLocation.insert and expand
function locationInsert(locations, loc, merge) {
	let length = locations.length,
		l = 0,
		r = length - 1

	function search(index, dir) {
		for (let i = index + dir; i >= -1 && i <= length; i += dir) {
			const loc2 = locations[((i % length) + length) % length]
			if (!loc2) break
			const pt1 = loc.getPoint(),
				pt2 = loc2.getPoint()
			if (!pt1 || !pt2) break
			const dx = pt1.x - pt2.x,
				dy = pt1.y - pt2.y
			if (sqrt(dx * dx + dy * dy) > GEOMETRIC_EPSILON) break
			if (loc.equals(loc2)) return loc2
		}
		return null
	}

	while (l <= r) {
		let m = (l + r) >>> 1,
			loc2 = locations[m],
			found
		if (merge && (found = loc.equals(loc2) ? loc2 : search(m, -1) || search(m, 1))) {
			if (loc._overlap) {
				found._overlap = found._intersection._overlap = true
			}
			return found
		}
		const path1 = loc.getPath(),
			path2 = loc2.getPath(),
			diff =
				path1 !== path2
					? path1._id - path2._id
					: loc.getIndex() + loc.getTime() - (loc2.getIndex() + loc2.getTime())
		if (diff < 0) r = m - 1
		else l = m + 1
	}
	locations.splice(l, 0, loc)
	return loc
}

function locationExpand(locations) {
	const expanded = locations.slice()
	for (let i = locations.length - 1; i >= 0; i--) locationInsert(expanded, locations[i]._intersection, false)
	return expanded
}

// ─── Intersection finder ────────────────────────────────────────────

function addLocation(locations, include, c1, t1, c2, t2, overlap) {
	const excludeStart = !overlap && c1.getPrevious() === c2,
		excludeEnd = !overlap && c1 !== c2 && c1.getNext() === c2,
		tMin = CURVETIME_EPSILON,
		tMax = 1 - tMin
	if (t1 !== null && t1 >= (excludeStart ? tMin : 0) && t1 <= (excludeEnd ? tMax : 1)) {
		if (t2 !== null && t2 >= (excludeEnd ? tMin : 0) && t2 <= (excludeStart ? tMax : 1)) {
			const loc1 = createCurveLocation(c1, t1, null, overlap),
				loc2 = createCurveLocation(c2, t2, null, overlap)
			loc1._intersection = loc2
			loc2._intersection = loc1
			if (!include || include(loc1)) locationInsert(locations, loc1, true)
		}
	}
}

function getConvexHull(dq0, dq1, dq2, dq3) {
	let p0 = [0, dq0],
		p1 = [1 / 3, dq1],
		p2 = [2 / 3, dq2],
		p3 = [1, dq3],
		dist1 = dq1 - (2 * dq0 + dq3) / 3,
		dist2 = dq2 - (dq0 + 2 * dq3) / 3,
		hull
	if (dist1 * dist2 < 0) {
		hull = [
			[p0, p1, p3],
			[p0, p2, p3],
		]
	} else {
		const distRatio = dist1 / dist2
		hull = [distRatio >= 2 ? [p0, p1, p3] : distRatio <= 0.5 ? [p0, p2, p3] : [p0, p1, p2, p3], [p0, p3]]
	}
	return (dist1 || dist2) < 0 ? hull.reverse() : hull
}

function clipConvexHull(hullTop, hullBottom, dMin, dMax) {
	if (hullTop[0][1] < dMin) return clipConvexHullPart(hullTop, true, dMin)
	if (hullBottom[0][1] > dMax) return clipConvexHullPart(hullBottom, false, dMax)
	return hullTop[0][0]
}

function clipConvexHullPart(part, top, threshold) {
	let px = part[0][0],
		py = part[0][1]
	for (let i = 1, l = part.length; i < l; i++) {
		const qx = part[i][0],
			qy = part[i][1]
		if (top ? qy >= threshold : qy <= threshold)
			return qy === threshold ? qx : px + ((threshold - py) * (qx - px)) / (qy - py)
		px = qx
		py = qy
	}
	return null
}

function addCurveIntersections(v1, v2, c1, c2, locations, include, flip, recursion, calls, tMin, tMax, uMin, uMax) {
	if (++calls >= 4096 || ++recursion >= 40) return calls
	let fatLineEpsilon = 1e-9,
		q0x = v2[0],
		q0y = v2[1],
		q3x = v2[6],
		q3y = v2[7],
		d1 = lineGetSignedDistance(q0x, q0y, q3x, q3y, v2[2], v2[3]),
		d2 = lineGetSignedDistance(q0x, q0y, q3x, q3y, v2[4], v2[5]),
		factor = d1 * d2 > 0 ? 3 / 4 : 4 / 9,
		dMin = factor * min(0, d1, d2),
		dMax = factor * max(0, d1, d2),
		dp0 = lineGetSignedDistance(q0x, q0y, q3x, q3y, v1[0], v1[1]),
		dp1 = lineGetSignedDistance(q0x, q0y, q3x, q3y, v1[2], v1[3]),
		dp2 = lineGetSignedDistance(q0x, q0y, q3x, q3y, v1[4], v1[5]),
		dp3 = lineGetSignedDistance(q0x, q0y, q3x, q3y, v1[6], v1[7]),
		hull = getConvexHull(dp0, dp1, dp2, dp3),
		top = hull[0],
		bottom = hull[1],
		tMinClip,
		tMaxClip

	if (
		(d1 === 0 && d2 === 0 && dp0 === 0 && dp1 === 0 && dp2 === 0 && dp3 === 0) ||
		(tMinClip = clipConvexHull(top, bottom, dMin, dMax)) == null ||
		(tMaxClip = clipConvexHull(top.reverse(), bottom.reverse(), dMin, dMax)) == null
	)
		return calls

	const tMinNew = tMin + (tMax - tMin) * tMinClip,
		tMaxNew = tMin + (tMax - tMin) * tMaxClip
	if (max(uMax - uMin, tMaxNew - tMinNew) < fatLineEpsilon) {
		const t = (tMinNew + tMaxNew) / 2,
			u = (uMin + uMax) / 2
		addLocation(locations, include, flip ? c2 : c1, flip ? u : t, flip ? c1 : c2, flip ? t : u)
	} else {
		v1 = curveGetPart(v1, tMinClip, tMaxClip)
		const uDiff = uMax - uMin
		if (tMaxClip - tMinClip > 0.8) {
			if (tMaxNew - tMinNew > uDiff) {
				const parts = curveSubdivide(v1, 0.5),
					t = (tMinNew + tMaxNew) / 2
				calls = addCurveIntersections(
					v2,
					parts[0],
					c2,
					c1,
					locations,
					include,
					!flip,
					recursion,
					calls,
					uMin,
					uMax,
					tMinNew,
					t
				)
				calls = addCurveIntersections(
					v2,
					parts[1],
					c2,
					c1,
					locations,
					include,
					!flip,
					recursion,
					calls,
					uMin,
					uMax,
					t,
					tMaxNew
				)
			} else {
				const parts = curveSubdivide(v2, 0.5),
					u = (uMin + uMax) / 2
				calls = addCurveIntersections(
					parts[0],
					v1,
					c2,
					c1,
					locations,
					include,
					!flip,
					recursion,
					calls,
					uMin,
					u,
					tMinNew,
					tMaxNew
				)
				calls = addCurveIntersections(
					parts[1],
					v1,
					c2,
					c1,
					locations,
					include,
					!flip,
					recursion,
					calls,
					u,
					uMax,
					tMinNew,
					tMaxNew
				)
			}
		} else {
			if (uDiff === 0 || uDiff >= fatLineEpsilon) {
				calls = addCurveIntersections(
					v2,
					v1,
					c2,
					c1,
					locations,
					include,
					!flip,
					recursion,
					calls,
					uMin,
					uMax,
					tMinNew,
					tMaxNew
				)
			} else {
				calls = addCurveIntersections(
					v1,
					v2,
					c1,
					c2,
					locations,
					include,
					flip,
					recursion,
					calls,
					tMinNew,
					tMaxNew,
					uMin,
					uMax
				)
			}
		}
	}
	return calls
}

function getCurveLineIntersections(v, px, py, vx, vy) {
	if (isZero(vx) && isZero(vy)) {
		const t = curveGetTimeOf(v, px, py)
		return t === null ? [] : [t]
	}
	const angle = Math.atan2(-vy, vx),
		sin = Math.sin(angle),
		cos = Math.cos(angle),
		rv = [],
		roots = []
	for (let i = 0; i < 8; i += 2) {
		const x = v[i] - px,
			y = v[i + 1] - py
		rv.push(x * cos - y * sin, x * sin + y * cos)
	}
	curveSolveCubic(rv, 1, 0, roots, 0, 1)
	return roots
}

function addCurveLineIntersections(v1, v2, c1, c2, locations, include, flip) {
	const x1 = v2[0],
		y1 = v2[1],
		x2 = v2[6],
		y2 = v2[7],
		roots = getCurveLineIntersections(v1, x1, y1, x2 - x1, y2 - y1)
	for (let i = 0, l = roots.length; i < l; i++) {
		const t1 = roots[i],
			p1 = curveGetPoint(v1, t1),
			t2 = curveGetTimeOf(v2, p1.x, p1.y)
		if (t2 !== null) {
			addLocation(locations, include, flip ? c2 : c1, flip ? t2 : t1, flip ? c1 : c2, flip ? t1 : t2)
		}
	}
}

function addLineIntersection(v1, v2, c1, c2, locations, include) {
	const pt = lineIntersect(v1[0], v1[1], v1[6], v1[7], v2[0], v2[1], v2[6], v2[7])
	if (pt) {
		addLocation(locations, include, c1, curveGetTimeOf(v1, pt.x, pt.y), c2, curveGetTimeOf(v2, pt.x, pt.y))
	}
}

function getOverlaps(v1, v2) {
	function getSquaredLineLength(v) {
		const x = v[6] - v[0],
			y = v[7] - v[1]
		return x * x + y * y
	}
	let timeEpsilon = CURVETIME_EPSILON,
		geomEpsilon = GEOMETRIC_EPSILON,
		straight1 = curveIsStraight(v1),
		straight2 = curveIsStraight(v2),
		straightBoth = straight1 && straight2,
		flip = getSquaredLineLength(v1) < getSquaredLineLength(v2),
		l1 = flip ? v2 : v1,
		l2 = flip ? v1 : v2,
		px = l1[0],
		py = l1[1],
		vx = l1[6] - px,
		vy = l1[7] - py
	if (
		lineGetDistance(px, py, vx, vy, l2[0], l2[1], true) < geomEpsilon &&
		lineGetDistance(px, py, vx, vy, l2[6], l2[7], true) < geomEpsilon
	) {
		if (
			!straightBoth &&
			lineGetDistance(px, py, vx, vy, l1[2], l1[3], true) < geomEpsilon &&
			lineGetDistance(px, py, vx, vy, l1[4], l1[5], true) < geomEpsilon &&
			lineGetDistance(px, py, vx, vy, l2[2], l2[3], true) < geomEpsilon &&
			lineGetDistance(px, py, vx, vy, l2[4], l2[5], true) < geomEpsilon
		) {
			straight1 = straight2 = straightBoth = true
		}
	} else if (straightBoth) {
		return null
	}
	if (straight1 ^ straight2) return null

	let v = [v1, v2],
		pairs = []
	for (let i = 0; i < 4 && pairs.length < 2; i++) {
		const i1 = i & 1,
			i2 = i1 ^ 1,
			t1 = i >> 1,
			t2 = curveGetTimeOf(v[i1], v[i2][t1 ? 6 : 0], v[i2][t1 ? 7 : 1])
		if (t2 != null) {
			const pair = i1 ? [t1, t2] : [t2, t1]
			if (!pairs.length || (abs(pair[0] - pairs[0][0]) > timeEpsilon && abs(pair[1] - pairs[0][1]) > timeEpsilon))
				pairs.push(pair)
		}
		if (i > 2 && !pairs.length) break
	}
	if (pairs.length !== 2) {
		pairs = null
	} else if (!straightBoth) {
		const o1 = curveGetPart(v1, pairs[0][0], pairs[1][0]),
			o2 = curveGetPart(v2, pairs[0][1], pairs[1][1])
		if (
			abs(o2[2] - o1[2]) > geomEpsilon ||
			abs(o2[3] - o1[3]) > geomEpsilon ||
			abs(o2[4] - o1[4]) > geomEpsilon ||
			abs(o2[5] - o1[5]) > geomEpsilon
		)
			pairs = null
	}
	return pairs
}

function getCurveIntersections(v1, v2, c1, c2, locations, include) {
	const epsilon = EPSILON
	if (
		max(v1[0], v1[2], v1[4], v1[6]) + epsilon > min(v2[0], v2[2], v2[4], v2[6]) &&
		min(v1[0], v1[2], v1[4], v1[6]) - epsilon < max(v2[0], v2[2], v2[4], v2[6]) &&
		max(v1[1], v1[3], v1[5], v1[7]) + epsilon > min(v2[1], v2[3], v2[5], v2[7]) &&
		min(v1[1], v1[3], v1[5], v1[7]) - epsilon < max(v2[1], v2[3], v2[5], v2[7])
	) {
		const overlaps = getOverlaps(v1, v2)
		if (overlaps) {
			for (let i = 0; i < 2; i++) {
				const overlap = overlaps[i]
				addLocation(locations, include, c1, overlap[0], c2, overlap[1], true)
			}
		} else {
			const straight1 = curveIsStraight(v1),
				straight2 = curveIsStraight(v2),
				straight = straight1 && straight2,
				flip2 = straight1 && !straight2,
				before = locations.length
			;(straight
				? addLineIntersection
				: straight1 || straight2
					? addCurveLineIntersections
					: addCurveIntersections)(
				flip2 ? v2 : v1,
				flip2 ? v1 : v2,
				flip2 ? c2 : c1,
				flip2 ? c1 : c2,
				locations,
				include,
				flip2,
				0,
				0,
				0,
				1,
				0,
				1
			)
			if (!straight || locations.length === before) {
				for (let i = 0; i < 4; i++) {
					const t1 = i >> 1,
						t2 = i & 1,
						i1 = t1 * 6,
						i2 = t2 * 6,
						dx = v1[i1] - v2[i2],
						dy = v1[i1 + 1] - v2[i2 + 1]
					if (sqrt(dx * dx + dy * dy) < epsilon) addLocation(locations, include, c1, t1, c2, t2)
				}
			}
		}
	}
	return locations
}

function getSelfIntersection(v1, c1, locations, include) {
	const info = curveClassify(v1)
	if (info.type === 'loop') {
		const roots = info.roots
		addLocation(locations, include, c1, roots[0], c1, roots[1])
	}
	return locations
}

function getIntersections(curves1, curves2, include, _returnFirst) {
	const self = !curves2
	if (self) curves2 = curves1
	const length1 = curves1.length,
		length2 = curves2.length,
		values1 = new Array(length1),
		values2 = self ? values1 : new Array(length2),
		locations = []

	for (let i = 0; i < length1; i++) values1[i] = curves1[i].getValues()
	if (!self) {
		for (let i = 0; i < length2; i++) values2[i] = curves2[i].getValues()
	}
	const boundsCollisions = findCurveBoundsCollisions(values1, values2, GEOMETRIC_EPSILON)
	for (let index1 = 0; index1 < length1; index1++) {
		const curve1 = curves1[index1],
			v1 = values1[index1]
		if (self) getSelfIntersection(v1, curve1, locations, include)
		const collisions1 = boundsCollisions[index1]
		if (collisions1) {
			for (let j = 0; j < collisions1.length; j++) {
				if (_returnFirst && locations.length) return locations
				const index2 = collisions1[j]
				if (!self || index2 > index1) {
					getCurveIntersections(v1, values2[index2], curve1, curves2[index2], locations, include)
				}
			}
		}
	}
	return locations
}

// ─── Core boolean algorithm ─────────────────────────────────────────

const operators = {
	unite: { 1: true, 2: true },
	intersect: { 2: true },
	subtract: { 1: true },
	exclude: { 1: true, '-1': true },
}

function filterIntersection(inter) {
	return inter.hasOverlap() || inter.isCrossing()
}

function linkIntersections(from, to) {
	let prev = from
	while (prev) {
		if (prev === to) return
		prev = prev._previous
	}
	while (from._next && from._next !== to) from = from._next
	if (!from._next) {
		while (to._previous) to = to._previous
		from._next = to
		to._previous = from
	}
}

function clearCurveHandles(curves) {
	for (let i = curves.length - 1; i >= 0; i--) curves[i].clearHandles()
}

function divideLocations(locations, include, clearLater) {
	let results = include && [],
		tMin = CURVETIME_EPSILON,
		tMax = 1 - tMin,
		clearHandles = false,
		clearCurves = clearLater || [],
		clearLookup = clearLater && {},
		renormalizeLocs,
		prevCurve,
		prevTime

	function getId(curve) {
		return curve._path._id + '.' + curve._segment1._index
	}

	for (let i = (clearLater && clearLater.length) - 1; i >= 0; i--) {
		const curve = clearLater[i]
		if (curve._path) clearLookup[getId(curve)] = true
	}

	for (let i = locations.length - 1; i >= 0; i--) {
		let loc = locations[i],
			time = loc._time,
			origTime = time,
			exclude = include && !include(loc),
			curve = loc._curve,
			segment
		if (curve) {
			if (curve !== prevCurve) {
				clearHandles = !curve.hasHandles() || (clearLookup && clearLookup[getId(curve)])
				renormalizeLocs = []
				prevTime = null
				prevCurve = curve
			} else if (prevTime >= tMin) {
				time /= prevTime
			}
		}
		if (exclude) {
			if (renormalizeLocs) renormalizeLocs.push(loc)
			continue
		} else if (include) {
			results.unshift(loc)
		}
		prevTime = origTime
		if (time < tMin) {
			segment = curve._segment1
		} else if (time > tMax) {
			segment = curve._segment2
		} else {
			const newCurve = curve.divideAtTime(time, true)
			if (clearHandles) clearCurves.push(curve, newCurve)
			segment = newCurve._segment1
			for (let j = renormalizeLocs.length - 1; j >= 0; j--) {
				const l = renormalizeLocs[j]
				l._time = (l._time - time) / (1 - time)
			}
		}
		loc._setSegment(segment)
		const inter = segment._intersection,
			dest = loc._intersection
		if (inter) {
			linkIntersections(inter, dest)
			let other = inter
			while (other) {
				linkIntersections(other._intersection, inter)
				other = other._next
			}
		} else {
			segment._intersection = dest
		}
	}
	if (!clearLater) clearCurveHandles(clearCurves)
	return results || locations
}

// ─── getWinding ─────────────────────────────────────────────────────

function getWinding(point, curves, dir, closed, dontFlip) {
	const curvesList = Array.isArray(curves) ? curves : curves[dir ? 'hor' : 'ver']
	let ia = dir ? 1 : 0,
		io = ia ^ 1,
		pv = [point.x, point.y],
		pa = pv[ia],
		po = pv[io],
		windingEpsilon = 1e-9,
		qualityEpsilon = 1e-6,
		paL = pa - windingEpsilon,
		paR = pa + windingEpsilon,
		windingL = 0,
		windingR = 0,
		pathWindingL = 0,
		pathWindingR = 0,
		onPath = false,
		onAnyPath = false,
		quality = 1,
		roots = [],
		vPrev,
		vClose

	function addWinding(v) {
		const o0 = v[io],
			o3 = v[io + 6]
		if (po < min(o0, o3) || po > max(o0, o3)) return
		const a0 = v[ia],
			a1 = v[ia + 2],
			a2 = v[ia + 4],
			a3 = v[ia + 6]
		if (o0 === o3) {
			if ((a0 < paR && a3 > paL) || (a3 < paR && a0 > paL)) onPath = true
			return
		}
		const t =
				po === o0
					? 0
					: po === o3
						? 1
						: paL > max(a0, a1, a2, a3) || paR < min(a0, a1, a2, a3)
							? 1
							: curveSolveCubic(v, io, po, roots, 0, 1) > 0
								? roots[0]
								: 1,
			a = t === 0 ? a0 : t === 1 ? a3 : curveGetPoint(v, t)[dir ? 'y' : 'x'],
			winding = o0 > o3 ? 1 : -1,
			windingPrev = vPrev[io] > vPrev[io + 6] ? 1 : -1,
			a3Prev = vPrev[ia + 6]
		if (po !== o0) {
			if (a < paL) pathWindingL += winding
			else if (a > paR) pathWindingR += winding
			else onPath = true
			if (a > pa - qualityEpsilon && a < pa + qualityEpsilon) quality /= 2
		} else {
			if (winding !== windingPrev) {
				if (a0 < paL) pathWindingL += winding
				else if (a0 > paR) pathWindingR += winding
			} else if (a0 != a3Prev) {
				if (a3Prev < paR && a > paR) {
					pathWindingR += winding
					onPath = true
				} else if (a3Prev > paL && a < paL) {
					pathWindingL += winding
					onPath = true
				}
			}
			quality /= 4
		}
		vPrev = v
		return (
			!dontFlip &&
			a > paL &&
			a < paR &&
			curveGetTangent(v, t)[dir ? 'x' : 'y'] === 0 &&
			getWinding(point, curves, !dir, closed, true)
		)
	}

	function handleCurve(v) {
		const o0 = v[io],
			o1 = v[io + 2],
			o2 = v[io + 4],
			o3 = v[io + 6]
		if (po <= max(o0, o1, o2, o3) && po >= min(o0, o1, o2, o3)) {
			let a0 = v[ia],
				a1 = v[ia + 2],
				a2 = v[ia + 4],
				a3 = v[ia + 6],
				monoCurves = paL > max(a0, a1, a2, a3) || paR < min(a0, a1, a2, a3) ? [v] : curveGetMonoCurves(v, dir),
				res
			for (let i = 0, l = monoCurves.length; i < l; i++) {
				if ((res = addWinding(monoCurves[i]))) return res
			}
		}
	}

	for (let i = 0, l = curvesList.length; i < l; i++) {
		let curve = curvesList[i],
			path = curve._path,
			v = curve.getValues(),
			res
		if (!i || curvesList[i - 1]._path !== path) {
			vPrev = null
			if (!path._closed) {
				const lastCurve = path.getLastCurve(),
					seg2 = lastCurve._segment2,
					seg1 = curve._segment1
				// Build closing curve values
				vClose = [
					seg2._point.x,
					seg2._point.y,
					seg2._point.x + (!closed ? 0 : seg2._handleOut.x),
					seg2._point.y + (!closed ? 0 : seg2._handleOut.y),
					seg1._point.x + (!closed ? 0 : seg1._handleIn.x),
					seg1._point.y + (!closed ? 0 : seg1._handleIn.y),
					seg1._point.x,
					seg1._point.y,
				]
				if (vClose[io] !== vClose[io + 6]) vPrev = vClose
			}
			if (!vPrev) {
				vPrev = v
				let prev = path.getLastCurve()
				while (prev && prev !== curve) {
					const v2 = prev.getValues()
					if (v2[io] !== v2[io + 6]) {
						vPrev = v2
						break
					}
					prev = prev.getPrevious()
				}
			}
		}
		if ((res = handleCurve(v))) return res
		if (i + 1 === l || curvesList[i + 1]._path !== path) {
			if (vClose && (res = handleCurve(vClose))) return res
			if (onPath && !pathWindingL && !pathWindingR) {
				pathWindingL = pathWindingR = path.isClockwise(closed) ^ dir ? 1 : -1
			}
			windingL += pathWindingL
			windingR += pathWindingR
			pathWindingL = pathWindingR = 0
			if (onPath) {
				onAnyPath = true
				onPath = false
			}
			vClose = null
		}
	}
	windingL = abs(windingL)
	windingR = abs(windingR)
	return {
		winding: max(windingL, windingR),
		windingL,
		windingR,
		quality,
		onPath: onAnyPath,
	}
}

// ─── propagateWinding ───────────────────────────────────────────────

function propagateWinding(segment, path1, path2, curveCollisionsMap, operator) {
	let chain = [],
		start = segment,
		totalLength = 0
	do {
		const curve = segment.getCurve()
		if (curve) {
			const length = curve.getLength()
			chain.push({ segment, curve, length })
			totalLength += length
		}
		segment = segment.getNext()
	} while (segment && !segment._intersection && segment !== start)

	let offsets = [0.5, 0.25, 0.75],
		wind = { winding: 0, quality: -1 },
		tMin = 1e-3,
		tMax = 1 - tMin
	for (let i = 0; i < offsets.length && wind.quality < 0.5; i++) {
		let length = totalLength * offsets[i]
		for (let j = 0, l = chain.length; j < l; j++) {
			const entry = chain[j],
				curveLength = entry.length
			if (length <= curveLength) {
				const curve = entry.curve,
					path = curve._path,
					parent = path._parent,
					operand = parent && parent._children ? parent : path,
					t = clamp(curve.getTimeAt(length), tMin, tMax),
					pt = curve.getPointAtTime(t),
					dir = abs(curve.getTangentAtTime(t).y) < Math.SQRT1_2
				let w = null
				if (operator.subtract && path2) {
					const otherPath = operand === path1 ? path2 : path1,
						pathWinding = otherPath._getWinding(pt, dir, true)
					if ((operand === path1 && pathWinding.winding) || (operand === path2 && !pathWinding.winding)) {
						if (pathWinding.quality < 1) continue
						else w = { winding: 0, quality: 1 }
					}
				}
				if (!w) {
					const collisions = curveCollisionsMap[path._id][curve.getIndex()]
					w = getWinding(pt, collisions, dir, true)
				}
				if (w.quality > wind.quality) wind = w
				break
			}
			length -= curveLength
		}
	}
	for (let j = chain.length - 1; j >= 0; j--) chain[j].segment._winding = wind
}

// ─── tracePaths ─────────────────────────────────────────────────────

function tracePaths(segments, operator) {
	let paths = [],
		starts

	function isValid(seg) {
		let winding
		return !!(
			seg &&
			!seg._visited &&
			(!operator ||
				(operator[(winding = seg._winding || {}).winding] &&
					!(operator.unite && winding.winding === 2 && winding.windingL && winding.windingR)))
		)
	}

	function isStart(seg) {
		if (seg) {
			for (let i = 0, l = starts.length; i < l; i++) if (seg === starts[i]) return true
		}
		return false
	}

	function visitPath(path) {
		const segs = path._segments
		for (let i = 0, l = segs.length; i < l; i++) segs[i]._visited = true
	}

	function getCrossingSegments(segment, collectStarts) {
		let inter = segment._intersection,
			start = inter,
			crossings = []
		if (collectStarts) starts = [segment]

		function collect(inter, end) {
			while (inter && inter !== end) {
				const other = inter._segment,
					path = other && other._path
				if (path) {
					const next = other.getNext() || path.getFirstSegment(),
						nextInter = next._intersection
					if (
						other !== segment &&
						(isStart(other) ||
							isStart(next) ||
							(next && isValid(other) && (isValid(next) || (nextInter && isValid(nextInter._segment)))))
					) {
						crossings.push(other)
					}
					if (collectStarts) starts.push(other)
				}
				inter = inter._next
			}
		}

		if (inter) {
			collect(inter)
			while (inter && inter._previous) inter = inter._previous
			collect(inter, start)
		}
		return crossings
	}

	segments.sort((seg1, seg2) => {
		const inter1 = seg1._intersection,
			inter2 = seg2._intersection,
			over1 = !!(inter1 && inter1._overlap),
			over2 = !!(inter2 && inter2._overlap),
			path1 = seg1._path,
			path2 = seg2._path
		return over1 ^ over2
			? over1
				? 1
				: -1
			: !inter1 ^ !inter2
				? inter1
					? 1
					: -1
				: path1 !== path2
					? path1._id - path2._id
					: seg1._index - seg2._index
	})

	for (let i = 0, l = segments.length; i < l; i++) {
		let seg = segments[i],
			valid = isValid(seg),
			path = null,
			finished = false,
			closed = true,
			branches = [],
			branch,
			visited,
			handleIn
		if (valid && seg._path._overlapsOnly) {
			const path1 = seg._path,
				path2 = seg._intersection._segment._path
			if (path1.compare(path2)) {
				if (path1.getArea()) paths.push(path1.clone())
				visitPath(path1)
				visitPath(path2)
				valid = false
			}
		}
		while (valid) {
			const first = !path,
				crossings = getCrossingSegments(seg, first),
				other = crossings.shift()
			finished = !first && (isStart(seg) || isStart(other))
			const cross = !finished && other
			if (first) {
				path = createPathWrapper()
				path._closed = false
				branch = null
			}
			if (finished) {
				if (seg.isFirst() || seg.isLast()) closed = seg._path._closed
				seg._visited = true
				break
			}
			if (cross && branch) {
				branches.push(branch)
				branch = null
			}
			if (!branch) {
				if (cross) crossings.push(seg)
				branch = {
					start: path._segments.length,
					crossings,
					visited: (visited = []),
					handleIn,
				}
			}
			if (cross) seg = other
			if (!isValid(seg)) {
				path.removeSegments(branch.start)
				for (let j = 0, k = visited.length; j < k; j++) visited[j]._visited = false
				visited.length = 0
				do {
					seg = branch && branch.crossings.shift()
					if (!seg || !seg._path) {
						seg = null
						branch = branches.pop()
						if (branch) {
							visited = branch.visited
							handleIn = branch.handleIn
						}
					}
				} while (branch && !isValid(seg))
				if (!seg) break
			}
			const next = seg.getNext()
			path.add(
				createSegment(
					{ x: seg._point.x, y: seg._point.y },
					handleIn,
					next && { x: seg._handleOut.x, y: seg._handleOut.y }
				)
			)
			seg._visited = true
			visited.push(seg)
			seg = next || seg._path.getFirstSegment()
			handleIn = next && { x: next._handleIn.x, y: next._handleIn.y }
		}
		if (finished) {
			if (closed) {
				const firstSeg = path.getFirstSegment()
				if (firstSeg && handleIn) {
					firstSeg._handleIn = { x: handleIn.x, y: handleIn.y }
				}
				path.setClosed(closed)
			}
			const area = path.getArea()
			if (area !== 0) paths.push(path)
		}
	}
	return paths
}

// ─── reorientPaths ──────────────────────────────────────────────────

function reorientPaths(paths, isInside, clockwise) {
	const length = paths && paths.length
	if (length) {
		const lookup = {}
		for (let i = 0; i < length; i++) {
			const path = paths[i]
			if (path) {
				lookup[path._id] = {
					container: null,
					winding: path.isClockwise() ? 1 : -1,
					index: i,
				}
			}
		}
		const sorted = paths.slice().sort((a, b) => {
			if (!a) return 1
			if (!b) return -1
			return abs(b.getArea()) - abs(a.getArea())
		})
		const first = sorted[0]
		// Build bounds for collision detection
		const sortedBounds = []
		for (let i = 0; i < sorted.length; i++) {
			if (sorted[i]) {
				const b = sorted[i].getBounds()
				sortedBounds.push([b.x, b.y, b.x + b.width, b.y + b.height])
			} else {
				sortedBounds.push([0, 0, 0, 0])
			}
		}
		const collisions = findBoundsCollisions(sortedBounds, null, GEOMETRIC_EPSILON)

		if (clockwise == null) clockwise = first && first.isClockwise()
		for (let i = 0; i < length; i++) {
			const path1 = sorted[i]
			if (!path1) continue
			let entry1 = lookup[path1._id],
				containerWinding = 0,
				indices = collisions[i]
			if (indices) {
				let point = null
				for (let j = indices.length - 1; j >= 0; j--) {
					if (indices[j] < i) {
						point = point || path1.getInteriorPoint()
						const path2 = sorted[indices[j]]
						if (path2 && path2.contains(point)) {
							const entry2 = lookup[path2._id]
							containerWinding = entry2.winding
							entry1.winding += containerWinding
							entry1.container = entry2.exclude ? entry2.container : path2
							break
						}
					}
				}
			}
			if (isInside(entry1.winding) === isInside(containerWinding)) {
				entry1.exclude = true
				paths[entry1.index] = null
			} else {
				const container = entry1.container
				path1.setClockwise(container ? !container.isClockwise() : clockwise)
			}
		}
	}
	return paths
}

// ─── resolveCrossings ────────────────────────────────────────────────

/**
 * Resolve self-intersections in a path or compound path. Ported from Paper.js Path#resolveCrossings(). Returns an array of PathWrappers with no self-intersections.
 */
function resolveCrossings(item) {
	const paths = item._children || [item]

	function hasOverlapWith(seg, path) {
		const inter = seg && seg._intersection
		return inter && inter._overlap && inter._curve._path === path
	}

	// Find all self-intersections (overlaps and crossings)
	let hasOverlaps = false,
		hasCrossings = false
	const allCurves = []
	for (let i = 0; i < paths.length; i++) {
		const crvs = paths[i].getCurves()
		for (let j = 0; j < crvs.length; j++) allCurves.push(crvs[j])
	}
	const intersections = locationExpand(
		getIntersections(
			allCurves,
			null,
			inter => (inter.hasOverlap() && (hasOverlaps = true)) || (inter.isCrossing() && (hasCrossings = true))
		)
	)

	if (!hasOverlaps && !hasCrossings) return paths

	const clearCurves = hasOverlaps && hasCrossings && []

	if (hasOverlaps) {
		const overlaps = divideLocations(intersections, inter => inter.hasOverlap(), clearCurves)
		for (let i = overlaps.length - 1; i >= 0; i--) {
			const overlap = overlaps[i],
				path = overlap._curve._path,
				seg = overlap._segment,
				prev = seg.getPrevious(),
				next = seg.getNext()
			if (hasOverlapWith(prev, path) && hasOverlapWith(next, path)) {
				seg.remove()
				prev._handleOut = { x: 0, y: 0 }
				next._handleIn = { x: 0, y: 0 }
				// If the remaining curve has no length, remove it too
				if (prev !== seg && !prev.getCurve().hasLength()) {
					next._handleIn = { x: prev._handleIn.x, y: prev._handleIn.y }
					prev.remove()
				}
			}
		}
	}

	if (hasCrossings) {
		divideLocations(
			intersections,
			hasOverlaps &&
				(inter => {
					const curve1 = inter.getCurve(),
						seg1 = inter.getSegment(),
						other = inter._intersection,
						curve2 = other._curve,
						seg2 = other._segment
					if (curve1 && curve2 && curve1._path && curve2._path) return true
					if (seg1) seg1._intersection = null
					if (seg2) seg2._intersection = null
				}),
			clearCurves
		)
		if (clearCurves) clearCurveHandles(clearCurves)
		// Collect all segments and trace clean paths
		const segments = []
		for (let i = 0; i < paths.length; i++) {
			const segs = paths[i]._segments
			for (let j = 0; j < segs.length; j++) segments.push(segs[j])
		}
		return tracePaths(segments)
	}

	return paths
}

/**
 * Prepare a path/compound for boolean ops: resolve self-intersections, reorient winding. Ported from Paper.js preparePath(). Returns a CompoundPathWrapper or PathWrapper ready for traceBoolean.
 */
function preparePath(item, evenodd) {
	const resolvedPaths = resolveCrossings(item)
	if (resolvedPaths.length === 1) return resolvedPaths[0]
	// Wrap multiple paths into a CompoundPathWrapper
	const compound = createCompoundPathWrapper()
	// Reorient so sub-paths have correct winding
	const reoriented = reorientPaths(resolvedPaths.slice(), evenodd ? w => !!(w & 1) : w => !!w, true)
	// Filter out nulled paths from reorientPaths
	for (let i = 0; i < reoriented.length; i++) {
		if (reoriented[i]) {
			reoriented[i]._parent = compound
			compound._children.push(reoriented[i])
		}
	}
	return compound
}

// ─── traceBoolean ───────────────────────────────────────────────────

function traceBoolean(path1, path2, operation, path2Evenodd) {
	// Resolve self-intersections before the binary operation
	path1 = preparePath(path1)
	if (path2) path2 = preparePath(path2, path2Evenodd)

	const operator = Object.assign({}, operators[operation])
	operator[operation] = true

	// Give both paths the same orientation except for subtract/exclude
	if (path2 && (operator.subtract || operator.exclude) ^ (path2.isClockwise() ^ path1.isClockwise())) path2.reverse()

	const _locs = getIntersections(path1.getCurves(), path2 && path2.getCurves(), filterIntersection)
	let crossings = divideLocations(locationExpand(_locs)),
		paths1 = getPaths(path1),
		paths2 = path2 && getPaths(path2),
		segments = [],
		curves = [],
		resultPaths

	function collectPaths(paths) {
		for (let i = 0, l = paths.length; i < l; i++) {
			const path = paths[i],
				segs = path._segments,
				crvs = path.getCurves()
			for (let j = 0; j < segs.length; j++) segments.push(segs[j])
			for (let j = 0; j < crvs.length; j++) curves.push(crvs[j])
			path._overlapsOnly = true
		}
	}

	function getCurvesForIndices(indices) {
		const list = []
		for (let i = 0, l = indices && indices.length; i < l; i++) list.push(curves[indices[i]])
		return list
	}

	if (crossings.length) {
		collectPaths(paths1)
		if (paths2) collectPaths(paths2)

		const curvesValues = new Array(curves.length)
		for (let i = 0, l = curves.length; i < l; i++) curvesValues[i] = curves[i].getValues()

		const curveCollisions = findCurveBoundsCollisions(curvesValues, curvesValues, 0, true)
		const curveCollisionsMap = {}
		for (let i = 0; i < curves.length; i++) {
			const curve = curves[i],
				id = curve._path._id,
				map = (curveCollisionsMap[id] = curveCollisionsMap[id] || {})
			map[curve.getIndex()] = {
				hor: getCurvesForIndices(curveCollisions[i].hor),
				ver: getCurvesForIndices(curveCollisions[i].ver),
			}
		}

		for (let i = 0, l = crossings.length; i < l; i++) {
			propagateWinding(crossings[i]._segment, path1, path2, curveCollisionsMap, operator)
		}
		for (let i = 0, l = segments.length; i < l; i++) {
			const segment = segments[i],
				inter = segment._intersection
			if (!segment._winding) {
				propagateWinding(segment, path1, path2, curveCollisionsMap, operator)
			}
			if (!(inter && inter._overlap)) segment._path._overlapsOnly = false
		}
		resultPaths = tracePaths(segments, operator)
	} else {
		resultPaths = reorientPaths(paths2 ? paths1.concat(paths2) : paths1.slice(), w => !!operator[w])
	}
	return resultPaths
}

// ─── Public API ─────────────────────────────────────────────────────

export function booleanSubtract(pathAOrContours, pathB) {
	const contoursA = Array.isArray(pathAOrContours[0]?.[0]) ? pathAOrContours : [pathAOrContours]
	const wrapperA = contoursToCompound(contoursA)
	const wrapperB = inputToWrapper(pathB)

	const resultPaths = traceBoolean(wrapperA, wrapperB, 'subtract')
	return resultToContours(resultPaths)
}

/**
 * Subtract pathB from pathA, treating pathB (the eraser) with evenodd winding. For a self-intersecting eraser stroke outline, evenodd excludes regions with winding=2 (loop interior, crossing strip) — only the stroke band (winding=1) is erased.
 */
export function booleanSubtractEvenodd(pathAOrContours, pathB) {
	const contoursA = Array.isArray(pathAOrContours[0]?.[0]) ? pathAOrContours : [pathAOrContours]
	const wrapperA = contoursToCompound(contoursA)
	const wrapperB = inputToWrapper(pathB)

	const resultPaths = traceBoolean(wrapperA, wrapperB, 'subtract', true)
	return resultToContours(resultPaths)
}

/** Convert input (curve8[] or curve8[][]) to a wrapper. */
function inputToWrapper(input) {
	const isMulti = Array.isArray(input[0]?.[0])
	return isMulti ? contoursToCompound(input) : curve8ToPath(input)
}

export function booleanUnite(pathA, pathB) {
	const wrapperA = inputToWrapper(pathA)
	const wrapperB = inputToWrapper(pathB)
	const resultPaths = traceBoolean(wrapperA, wrapperB, 'unite')
	return resultToContours(resultPaths)
}

export function booleanIntersect(pathA, pathB) {
	const wrapperA = inputToWrapper(pathA)
	const wrapperB = inputToWrapper(pathB)
	const resultPaths = traceBoolean(wrapperA, wrapperB, 'intersect')
	return resultToContours(resultPaths)
}

export function booleanExclude(pathA, pathB) {
	const wrapperA = inputToWrapper(pathA)
	const wrapperB = inputToWrapper(pathB)
	const resultPaths = traceBoolean(wrapperA, wrapperB, 'exclude')
	return resultToContours(resultPaths)
}

/**
 * Resolve self-intersections in a set of contours using nonzero winding. Input: curve8[][] — one or more contours (e.g. eraser stroke outline). Output: curve8[][] — a single clean non-self-intersecting outer contour.
 *
 * Uses nonzero fill rule: the enclosed region of a looping stroke has winding=2 (nonzero → inside), so it is absorbed into the outer contour rather than becoming a hole. Result is always a single contour representing the actual swept area of the stroke.
 */
export function resolveContours(curves) {
	if (!curves || !curves.length) return curves
	const wrapper = inputToWrapper(curves)
	const resolvedPaths = resolveCrossings(wrapper)
	if (resolvedPaths.length === 1) return resultToContours(resolvedPaths)
	// Use nonzero winding: inner lobe (winding=2, isInside=true, same as container winding=1, isInside=true) gets excluded → compound ends up with 1 outer child.
	const compound = createCompoundPathWrapper()
	const reoriented = reorientPaths(resolvedPaths.slice(), w => w !== 0, true)
	for (let i = 0; i < reoriented.length; i++) {
		if (reoriented[i]) {
			reoriented[i]._parent = compound
			compound._children.push(reoriented[i])
		}
	}
	return resultToContours([compound])
}

// Re-export primitives that other modules depend on
export { curveGetPoint as evaluateBezierPoint }
